import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getStoredAuthToken } from "../../../utils/authSession";
import corretorDashboardService from "../../corretorDashboard/services/corretorDashboard.service";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const ERROR_MESSAGES = {
  LEAD_NAO_ASSOCIADO: "Este lead nao esta mais associado ao seu usuario.",
  ATENDIMENTO_ATIVO_EXISTENTE: "Este lead ja possui um atendimento ativo.",
  CONFLITO_ATENDIMENTO: "A conversa foi alterada por outro usuario. Atualize e tente novamente.",
  ATENDIMENTO_ENCERRADO: "Este atendimento ja foi encerrado. Inicie um novo atendimento para enviar outra mensagem.",
  JANELA_24H_EXPIRADA: "A janela de 24 horas terminou. Use um template aprovado para continuar.",
  SEM_MENSAGEM_USUARIO_RECENTE: "Nao ha uma mensagem recente do lead. Use um template aprovado.",
  TEMPLATE_NAO_APROVADO: "Este template nao esta aprovado para envio.",
  TEMPLATE_NAO_ENCONTRADO: "O template selecionado nao foi encontrado.",
  TEMPLATE_REPETIDO_24H: "Este template ja foi enviado recentemente para o lead.",
  AGUARDANDO_NOVA_MENSAGEM_USUARIO: "Aguarde uma nova mensagem do lead antes de enviar novamente.",
  QUALIDADE_CONTA_NAO_VERDE: "O envio esta indisponivel devido a qualidade da conta do WhatsApp.",
  QUALIDADE_CONTA_NAO_CONFIRMADA: "Nao foi possivel confirmar a qualidade da conta do WhatsApp.",
  IDEMPOTENCY_KEY_REUTILIZADA: "Este envio ja foi processado. Atualize a conversa antes de tentar novamente.",
  PERMISSAO_NEGADA: "Voce nao tem permissao para realizar esta acao.",
};

const getAuthContext = () => {
  const rawToken = getStoredAuthToken();
  if (!rawToken) throw new Error("Token nao encontrado.");

  const token = rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
  const decoded = jwtDecode(token);

  return {
    token,
    idUsuario: Number(decoded?.id_usuario ?? decoded?.idUsuario ?? 0) || null,
    role: String(decoded?.role || "").toLowerCase(),
    readonly: decoded?.readonly === true,
  };
};

const requestConfig = (extraHeaders = {}) => ({
  headers: {
    Authorization: `Bearer ${getAuthContext().token}`,
    ...extraHeaders,
  },
});

const getErrorMessage = (error, fallback) => {
  if (!error?.response) {
    return "A API nao respondeu. Verifique a conexao e tente novamente.";
  }

  const data = error.response.data;
  const code = data?.codigo;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (data?.mensagem) return data.mensagem;
  if (data?.message) return data.message;

  if (error.response.status === 403) return ERROR_MESSAGES.PERMISSAO_NEGADA;
  if (error.response.status === 409) return ERROR_MESSAGES.CONFLITO_ATENDIMENTO;
  if (error.response.status === 422) return "O envio foi bloqueado pelas regras do WhatsApp.";
  if (error.response.status === 502) return "Nao foi possivel comunicar com o WhatsApp neste momento.";
  if (error.response.status === 503) return "O servico esta temporariamente indisponivel.";
  return fallback;
};

const getWhatsappCredentials = async () => {
  const response = await axios.get(`${baseURL}/aidirect/whatsapp/credenciais`, {
    ...requestConfig(),
    params: { produtoOrigem: "CORR" },
  });
  return response.data;
};

const getContacts = async ({ ativo, busca, idSituacao, limit = 50, offset = 0 } = {}) => {
  const params = { produtoOrigem: "CORR", limit, offset };
  if (typeof ativo === "boolean") params.ativo = ativo;
  if (busca?.trim()) params.busca = busca.trim();
  if (idSituacao) params.idSituacao = idSituacao;

  const response = await axios.get(`${baseURL}/aidirect/lista-contatos`, {
    ...requestConfig(),
    params,
  });
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

const getSituacoes = () => corretorDashboardService.getSituacoes();

const getMessages = async ({ idLeadCrm, limit = 100, offset = 0 }) => {
  const response = await axios.get(`${baseURL}/aidirect/mensagens`, {
    ...requestConfig(),
    params: { produtoOrigem: "CORR", idLeadCrm, limit, offset },
  });
  return Array.isArray(response.data) ? response.data : [];
};

const getTemplates = async ({ limit = 50, after } = {}) => {
  const params = { produtoOrigem: "CORR", limit };
  if (after) params.after = after;

  const response = await axios.get(`${baseURL}/aidirect/templates`, {
    ...requestConfig(),
    params,
  });
  return response.data || { data: [], after: null, before: null };
};

const assumeAndSend = async (payload) => {
  const response = await axios.post(
    `${baseURL}/aidirect/atendimentos/assumir-enviar`,
    { produtoOrigem: "CORR", ...payload },
    requestConfig()
  );
  return response.data;
};

const sendMessage = async ({ idAtendimento, texto }) => {
  const response = await axios.post(
    `${baseURL}/aidirect/mensagens`,
    { idAtendimento, texto },
    requestConfig()
  );
  return response.data;
};

const sendTemplate = async (payload) => {
  const response = await axios.post(
    `${baseURL}/aidirect/templates/enviar`,
    payload,
    requestConfig()
  );
  return response.data;
};

const closeAttendance = async (idAtendimento) => {
  const response = await axios.patch(
    `${baseURL}/aidirect/atendimentos/encerrar`,
    { idAtendimento },
    requestConfig()
  );
  return response.data;
};

const aiDirectCorretorService = {
  getAuthContext,
  getErrorMessage,
  getWhatsappCredentials,
  getContacts,
  getSituacoes,
  getMessages,
  getTemplates,
  assumeAndSend,
  sendMessage,
  sendTemplate,
  closeAttendance,
};

export default aiDirectCorretorService;
